import _format from "date-fns/format";
import _getDay from "date-fns/getDay";
import _isToday from "date-fns/isToday";
import _isFuture from "date-fns/isFuture";
import _isSameDay from "date-fns/isSameDay";
import _isYesterday from "date-fns/isYesterday";
import _differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import _formatDistanceToNow from "date-fns/formatDistanceToNow";

import {useAunoaI18n} from "./useAunoaI18n";
import {useLocale} from './useLocale';
import {isString} from "./inspect";

type DateType = Date | number | string;

interface FormatDistanceOptions {
    includeSeconds?: boolean;
    addSuffix?: boolean;
    locale?: string;
}

const ensureDate = (date: DateType): Date | number =>
    isString(date) ? new Date(date) : date;

export const useDateTimeUtils = () => {

    const {t} = useAunoaI18n();
    const {getDateFnsLocale} = useLocale()
    
    const ensureOptions = (options?: FormatDistanceOptions) => ({
        ...options,
        locale: getDateFnsLocale(options?.locale)
    })

    const getDay = (date: DateType) => _getDay(ensureDate(date))
    const isToday = (date: DateType) => _isToday(ensureDate(date))
    const isFuture = (date: DateType) => _isFuture(ensureDate(date))
    const isYesterday = (date: DateType) => _isYesterday(ensureDate(date))

    const isSameDay = (dateLeft: DateType, dateRight: DateType) =>
        _isSameDay(ensureDate(dateLeft), ensureDate(dateRight))

    const dateFormat = (date: DateType, format: string) =>
        _format(ensureDate(date), format, {locale: getDateFnsLocale()});

    const formatDistanceToNow = (date: DateType, options?: FormatDistanceOptions) =>
        _formatDistanceToNow(ensureDate(date), ensureOptions(options));

    const differenceInCalendarDays = (dateLeft: DateType, dateRight: DateType) =>
        _differenceInCalendarDays(ensureDate(dateLeft), ensureDate(dateRight));

    const simplifyDate = (date: DateType, todayAsTime = false) => {
        date = ensureDate(date);
        const options = {locale: getDateFnsLocale()};
        if (_isToday(date)) {
            return todayAsTime
                ? _format(date, "p", options)
                : t("Aunoa.Date.Today");
        }
        if (_isYesterday(date)) {
            return t("Aunoa.Date.Yesterday");
        }
        const days = _differenceInCalendarDays(date, Date.now());
        return days < -1 && days > -7
            ? _format(date, "EEEE", options)
            : _format(date, "PP", options);
    }

    const simplifyDateAtTime = (date: DateType) => {
        date = ensureDate(date);
        const timeStr = _format(date, "p", {locale: getDateFnsLocale()});
        const dateStr = _format(date, "PP", {locale: getDateFnsLocale()});
        if (_isToday(date)) {
            return t("Aunoa.Date.TodayAt", {time: timeStr});
        }
        if (_isYesterday(date)) {
            return t("Aunoa.Date.YesterdayAt", {time: timeStr});
        }
        return t("Aunoa.Date.At", {time: timeStr, date: dateStr});
    }

    const lastYears = (count: number) => {
        const y = new Date().getFullYear();
        return [...Array(count).keys()].map(i => y - i);
    }

    return {
        formatDistanceToNow,
        dateFormat,

        getDay,
        isToday,
        isFuture,
        isSameDay,
        isYesterday,
        simplifyDate,
        simplifyDateAtTime,
        differenceInCalendarDays,
        lastYears
    }
}